import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/app-layout';
import { WelcomeDialog } from '@/components/welcome-dialog';
import { AppRoutes, ROUTES } from '@/routes';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { DemoSessionProvider } from '@/providers/DemoSessionProvider';
import { ProjectFormProvider } from '@/providers/ProjectFormProvider';
import { FieldConfigProvider } from '@/contexts/FieldConfigContext';
import { HeaderActionsProvider } from '@/contexts/HeaderActionsContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { TemplateProvider } from '@/contexts/TemplateContext';
import { InvoiceTemplateProvider } from '@/contexts/InvoiceTemplateContext';
import { ProjectStatusProvider } from '@/contexts/ProjectStatusContext';
import { BootstrapGate } from '@/components/BootstrapGate';
import { PageViewTracker } from '@/components/PageViewTracker';
import { IntercomProvider } from '@/providers/IntercomProvider';
import { useAuth } from '@/providers/AuthProvider';
import type { IntercomUser } from '@/types/intercom';
// TEMPORARY - DEMO ONLY: Developer tools for error demonstration
import { DeveloperToolsDialog } from '@/components/developer-tools-dialog';
import { ErrorDisplay } from '@/components/error';
import { createAppError, type AppError } from '@/types/error.types';
import { useDataMode } from '@/contexts/DataModeContext';

// Helper to convert route path to page name for preferences
const routeToPageName = (pathname: string): string => {
  // Remove leading slash and get first segment
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'dashboard';
  
  // Handle project details route
  if (segments[0] === 'projects' && segments.length > 1) {
    return 'projects';
  }
  
  // Handle settings sub-routes
  if (segments[0] === 'settings' && segments.length > 1) {
    return 'settings';
  }
  
  return segments[0];
};

// Helper to convert page name to route path
const pageNameToRoute = (pageName: string): string => {
  const routeMap: Record<string, string> = {
    'dashboard': ROUTES.DASHBOARD,
    'projects': ROUTES.PROJECTS,
    'portfolio': ROUTES.PORTFOLIO,
    'utilisation': ROUTES.UTILISATION,
    'wip-reporting': ROUTES.WIP_REPORTING,
    'profitability': ROUTES.PROFITABILITY,
    'budgets': ROUTES.BUDGETS,
    'resources': ROUTES.RESOURCES,
    'directory': ROUTES.DIRECTORY,
    'roles-rates': ROUTES.ROLES_RATES,
    'gantt': ROUTES.GANTT,
    'project-transactions': ROUTES.PROJECT_TRANSACTIONS,
    'audit-log': ROUTES.AUDIT_LOG,
    'settings': ROUTES.SETTINGS,
    'settings-general': ROUTES.SETTINGS_GENERAL,
    'settings-project': ROUTES.SETTINGS_PROJECT,
    'settings-user-preferences': ROUTES.SETTINGS_USER_PREFERENCES,
    'settings-integrations': ROUTES.SETTINGS_INTEGRATIONS,
    'settings-feature-labs': ROUTES.SETTINGS_FEATURE_LABS,
    'settings-user-permissions': ROUTES.SETTINGS_USER_PERMISSIONS,
    'settings-templates': ROUTES.SETTINGS_TEMPLATES,
    'settings-notifications': ROUTES.SETTINGS_NOTIFICATIONS,
    'settings-finance': ROUTES.SETTINGS_FINANCE,
    'project-templates': ROUTES.PROJECT_TEMPLATES,
    'user-preferences': ROUTES.USER_PREFERENCES,
    'category-management': ROUTES.CATEGORY_MANAGEMENT,
    'project-creation-setup': ROUTES.PROJECT_CREATION_SETUP,
    'error-handling-demo': ROUTES.ERROR_HANDLING_DEMO,
    'column-configurator-demo': ROUTES.COLUMN_CONFIGURATOR_DEMO,
    'responsive-table-demo': ROUTES.RESPONSIVE_TABLE_DEMO,
  };
  
  return routeMap[pageName] || ROUTES.DASHBOARD;
};

function AppContent() {
  const { preferences, updatePreferences } = useUserPreferences();
  // Note: headerActions, showBackButton, backButtonPath from useHeaderActions() are no longer used
  // as the new AppLayout component handles navigation differently via LeftMenu
  const location = useLocation();
  const navigate = useNavigate();
  // TEMPORARY - DEMO ONLY: Access data mode for developer tools
  const { isDemoMode } = useDataMode();
  
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // TEMPORARY - DEMO ONLY: Developer tools dialog state
  const [showDevTools, setShowDevTools] = useState(false);
  // TEMPORARY - DEMO ONLY: Demo application error state (shown as overlay without crashing the app)
  const [demoAppError, setDemoAppError] = useState<AppError | null>(null);
  
  // Get current page from route
  const currentPage = routeToPageName(location.pathname);
  
  // Initialize and handle initial navigation
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      
      // Only navigate if we're at the root path
      if (location.pathname === '/' || location.pathname === '') {
        // Determine the initial page to navigate to
        const getInitialPage = () => {
          if (preferences.rememberLastPage && preferences.lastVisitedPage) {
            return preferences.lastVisitedPage;
          }
          return preferences.defaultLandingPage;
        };
        
        const initialPage = getInitialPage();
        const initialRoute = pageNameToRoute(initialPage);
        
        // Navigate to the preferred landing page
        // Use replace to avoid adding to history stack
        navigate(initialRoute, { replace: true });
      }
    }
  }, [hasInitialized, preferences, navigate, location.pathname]);
  
  // TEMPORARY - DEMO ONLY: Invisible keyboard shortcut for developer tools
  // Only enabled in development mode AND when using demo data
  useEffect(() => {
    const isDevelopment = import.meta.env.DEV;
    
    // Only enable in dev mode AND demo mode
    if (!isDevelopment || !isDemoMode) {
      return;
    }
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+D (or Cmd+Shift+D on Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setShowDevTools(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDemoMode]);
  
  // Update last visited page when route changes (but not on settings pages)
  useEffect(() => {
    if (hasInitialized && preferences.rememberLastPage) {
      const page = routeToPageName(location.pathname);
      if (page !== 'user-preferences' && page !== 'settings') {
        updatePreferences({ lastVisitedPage: page });
      }
    }
  }, [location.pathname, preferences.rememberLastPage, hasInitialized, updatePreferences]);

  // TEMPORARY - DEMO ONLY: Handler for triggering demo application errors
  const handleTriggerDemoError = (error: Error) => {
    const appError = createAppError(error);
    setDemoAppError(appError);
  };

  // TEMPORARY - DEMO ONLY: Handler for closing demo error (X button - returns to drawer)
  const handleCloseDemoError = () => {
    setDemoAppError(null);
    // Keep the drawer open - user returns to the drawer
  };

  // TEMPORARY - DEMO ONLY: Handler for retry/reload actions (closes both error and drawer)
  const handleDismissError = () => {
    setDemoAppError(null);
    setShowDevTools(false); // Close the drawer when using Try Again or Reload
  };

  // Note: projectContext was previously used for AppLayout header but is no longer needed
  // as the new AppLayout component handles project context differently via LeftMenu and breadcrumbs

  return (
    <>
      <PageViewTracker />
      <WelcomeDialog />
      {/* TEMPORARY - DEMO ONLY: Developer tools dialog for error demonstration */}
      <DeveloperToolsDialog 
        open={showDevTools} 
        onOpenChange={setShowDevTools}
        onTriggerError={handleTriggerDemoError}
        hasActiveError={!!demoAppError}
      />
      {/* Main app content with conditional blur when dev tools is open (and no error) */}
      <div className={showDevTools && !demoAppError ? 'blur-sm pointer-events-none' : ''}>
        <AppLayout 
          currentPage={currentPage}
        >
          <AppRoutes />
        </AppLayout>
      </div>
      {/* TEMPORARY - DEMO ONLY: Demo application error overlay - rendered on top of everything */}
      {demoAppError && (
        <ErrorDisplay
          error={demoAppError}
          recovery={{
            canRetry: true,
            onRetry: handleDismissError, // Try Again closes both error and drawer
            canReload: true,
            onReload: handleDismissError, // Reload closes both error and drawer
          }}
          onClose={handleCloseDemoError} // X button only closes error, keeps drawer open
        />
      )}
    </>
  );
}

function useIntercomUser(): { intercomUser: IntercomUser | null } {
    const { authenticatedUser, instanceId } = useAuth();
    const email = authenticatedUser?.email;
    if (!email || !instanceId) {
        return { intercomUser: null };
    }
    return {
        intercomUser: { email, instanceId, name: authenticatedUser?.name },
    };
}

function IntercomWrapper({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
    const { intercomUser } = useIntercomUser();
    return <IntercomProvider user={intercomUser}>{children}</IntercomProvider>;
}

export default function App() {
  return (
    <DemoSessionProvider>
      <HeaderActionsProvider>
        <IntercomWrapper>
          <BootstrapGate>
            <AppContent />
          </BootstrapGate>
        </IntercomWrapper>
      </HeaderActionsProvider>
    </DemoSessionProvider>
  );
}

// Wrapper component that provides context providers inside the router
export function AppWithProviders() {
  return (
    <CompanyProvider>
      <ProjectStatusProvider>
        <TemplateProvider>
          <InvoiceTemplateProvider>
            <FieldConfigProvider>
              <ProjectFormProvider>
                <App />
              </ProjectFormProvider>
            </FieldConfigProvider>
          </InvoiceTemplateProvider>
        </TemplateProvider>
      </ProjectStatusProvider>
    </CompanyProvider>
  );
}
